
import React, { useRef, useEffect, useImperativeHandle, forwardRef, useState, useCallback } from 'react';

export interface WebcamControls {
  capture: () => void;
}

interface WebcamHandlerProps {
  isActive: boolean;
  onCaptureSuccess: (imageBase64: string) => void;
  onError: (message: string) => void;
}

const WebcamHandler = forwardRef<WebcamControls, WebcamHandlerProps>(
  ({ isActive, onCaptureSuccess, onError }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [webcamError, setWebcamError] = useState<string | null>(null);

    const startStream = useCallback(async () => {
      setWebcamError(null);
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const mediaStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        } else {
          throw new Error("getUserMedia non è supportato dal tuo browser.");
        }
      } catch (err) {
        console.error("Errore accesso webcam:", err);
        const message = err instanceof Error ? err.message : "Errore sconosciuto durante l'accesso alla webcam.";
        setWebcamError(`Impossibile accedere alla webcam: ${message}. Controlla i permessi.`);
        onError(`Impossibile accedere alla webcam: ${message}. Controlla i permessi.`);
      }
    }, [onError]);

    const stopStream = useCallback(() => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
      }
    }, [stream]);

    useEffect(() => {
      if (isActive) {
        startStream();
      } else {
        stopStream();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]); // startStream and stopStream are memoized

    useEffect(() => {
      // Cleanup on unmount
      return () => {
        stopStream();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // stopStream is memoized

    useImperativeHandle(ref, () => ({
      capture: () => {
        if (videoRef.current && canvasRef.current && stream) {
          const video = videoRef.current;
          const canvas = canvasRef.current;

          // Ensure canvas dimensions match video to avoid distortion
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          const context = canvas.getContext('2d');
          if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9); // Use JPEG with 90% quality
            onCaptureSuccess(imageDataUrl);
          } else {
            onError("Impossibile ottenere il contesto 2D dalla canvas.");
          }
        } else {
          if (!stream) {
            onError("Webcam non attiva o stream non disponibile per la cattura.");
          } else {
            onError("Elementi video o canvas non pronti per la cattura.");
          }
        }
      },
    }));

    return (
      <div className="relative w-full aspect-video bg-slate-700 rounded-lg overflow-hidden shadow-inner">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted // Good practice for user-initiated video
          className={`w-full h-full object-cover transition-opacity duration-300 ${isActive && stream ? 'opacity-100' : 'opacity-0'}`}
        />
        {(!isActive || webcamError || (isActive && !stream && !webcamError)) && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                {webcamError ? (
                    <p className="text-red-400">{webcamError}</p>
                ) : isActive && !stream ? (
                    <p className="text-slate-400">Avvio webcam in corso...</p>
                ) : (
                    <>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 text-slate-500 mb-2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                        </svg>
                        <p className="text-slate-400">Webcam non attiva. Clicca "Avvia Webcam".</p>
                    </>
                )}
            </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }
);

WebcamHandler.displayName = 'WebcamHandler';
export default WebcamHandler;
